import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Eye, AlertCircle, CheckCircle2, History, FileEdit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { FoilOptionsPicker } from '../../components/FoilOptionsPicker';

/**
 * Build the descriptive sentence under the "Preview My Sticker" title based
 * on the product's material (holographic ring, glitter ring, transparency
 * checkerboard, foil ring, etc.) and cutline shape (custom traced vs standard).
 */
const buildPreviewSubtitle = (order) => {
  const mat = order?.items?.[0]?.material?.toLowerCase?.() || '';
  const ptype = order?.items?.[0]?.product_type?.toLowerCase?.() || '';
  const probe = `${mat} ${ptype}`;
  const cut = order?.cutline_shape === 'custom' ? ' traced to your artwork' : '';

  let ring = '';
  if (probe.includes('holographic')) ring = ' and the holographic ring';
  else if (probe.includes('glitter')) ring = ' and the glitter ring';
  else if (probe.includes('glow')) ring = ' and the glow-in-the-dark ring';
  else if (
    probe.includes('clear') ||
    probe.includes('transparent') ||
    probe.includes('dtf') ||
    probe.includes('image transfer') ||
    probe.includes('static cling') ||
    probe.includes('temp tattoo')
  ) {
    ring =
      ' and the transparency checkerboard (the checkered area shows where your sticker is see-through)';
  } else if (probe.includes('foil') || probe.includes('gold & silver')) {
    ring = ' and the foil ring (adjust the color below to regenerate)';
  }

  return `See how your sticker will look with the magenta cut-line${cut}${ring}. This is what goes to production.`;
};

/**
 * The big preview card in the middle of the Review Order page. Orchestrates:
 *   - Foil options picker (only for foil products)
 *   - "Generate Sticker Preview" button (loads proof PDF → PNG)
 *   - Hover-to-reveal clean sticker shot (cross-fade animation)
 *   - Lifestyle AI gallery (see it in real life)
 *   - "Approve Proof & Submit Order" one-click CTA
 *   - "Regenerate Preview" refresh button
 *
 * All state is owned by the parent (ReviewOrderPage) — this card is
 * purely presentational and dispatches callbacks up.
 */
export const StickerPreviewCard = ({
  order,
  hasArtwork,
  isFoilProduct,
  submitting,
  // Proof preview state
  previewUrl,
  previewLoading,
  previewError,
  cleanUrl,
  hovering,
  // Version history (carousel under the proof)
  previewVersions = [],
  activeVersion = 0,
  onSelectVersion,
  onLoadPreview,
  onSetHovering,
  // Foil picker
  onFoilChange,
  // Approve/submit
  onApproveSubmit,
  // Payment-aware button label inputs. When pay_now + unpaid the
  // button reads "Pay $X.XX & Submit Order" so the customer sees the
  // exact next step. Net-30 invoice gets a distinct label, captured
  // payments get the one-click finisher. Defaults to the original
  // copy when no context is supplied (older callers).
  billingMode = 'pay_now',
  paymentStatus = '',
  formattedTotal = '',
  // Admin bypass label — staff users see "Submit Order (Admin)" on
  // the approve button instead of being nudged to authorize a card
  // or settle a Net-30 invoice that doesn't apply to them.
  isAdmin = false,
  // Routes the customer to /upload-artwork when they landed on Review
  // without any artwork attached. Enables the prominent CTA inside the
  // "No artwork attached yet" banner.
  onUploadArtworkClick,
  // iter114s.28 — Manual-proof escape hatch. When the customer doesn't
  // trust the system-generated dieline they can submit the order with
  // a flag that asks fulfilment to send a manually-prepared PDF proof
  // before going to print. Wired in ReviewOrderPage to
  // handleSubmitOrder({proofApproved:false}) — same flow as the
  // standard CostSummary "Submit" path but exposed alongside the
  // approve-and-skip button so the choice is explicit.
  onRequestManualProof,
  // Auto-trigger the proof preview on first mount so customers land on
  // a rendered sticker rather than a "click to generate" CTA. Only fires
  // once per card instance and only when the preview isn't already
  // loaded/loading/errored.
  autoLoad = false,
}) => {
  const subtitle = buildPreviewSubtitle(order);
  const hasMultipleVersions = previewVersions.length > 1;
  const autoLoadFiredRef = useRef(false);
  useEffect(() => {
    if (!autoLoad || autoLoadFiredRef.current) return;
    if (!hasArtwork) return;
    if (previewUrl || previewLoading || previewError) return;
    autoLoadFiredRef.current = true;
    onLoadPreview?.();
  }, [autoLoad, hasArtwork, previewUrl, previewLoading, previewError, onLoadPreview]);
  const foilValue = useMemo(
    () => ({
      foilType: order?.foil_type || 'gold',
      foilColorHex: order?.foil_color_hex || null,
    }),
    [order?.foil_type, order?.foil_color_hex]
  );

  // Approve-button label adapts to billing-mode + payment status so
  // the customer always sees the exact next step on the button itself.
  // Computed here (instead of inside the JSX) so the babel JSX
  // transform never has trouble closing over `billingMode` etc.
  let approveButtonLabel;
  if (submitting) {
    approveButtonLabel = 'Submitting…';
  } else if (isAdmin) {
    // Admin bypass — no payment, no Net-30 invoice. The order goes
    // straight into the production queue with `submitted_by_admin=true`
    // stamped server-side so AR aging filters it out.
    approveButtonLabel = 'Submit Order (Admin)';
  } else {
    const mode = (billingMode || 'pay_now').toLowerCase();
    const status = (paymentStatus || '').toLowerCase();
    if (mode === 'invoice') {
      approveButtonLabel = 'Submit on Net-30 Invoice';
    } else if (status === 'captured') {
      approveButtonLabel = formattedTotal
        ? `Approve & Capture ${formattedTotal}`
        : 'Approve & Capture Funds';
    } else if (status === 'authorized') {
      approveButtonLabel = 'Approve Proof & Submit Order';
    } else {
      approveButtonLabel = formattedTotal
        ? `Pay ${formattedTotal} & Submit Order`
        : 'Pay & Submit Order';
    }
  }

  return (
    <div
      className="bg-white rounded-lg shadow-lg p-6"
      data-testid="preview-sticker-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-[#4A7C59]" />
        <h2 className="text-xl font-semibold text-gray-800">Preview My Sticker</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">{subtitle}</p>

      {isFoilProduct && (
        <div className="mb-4 p-4 bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-lg">
          <FoilOptionsPicker
            compact
            value={foilValue}
            onChange={onFoilChange}
          />
        </div>
      )}

      <PreviewContent
        hasArtwork={hasArtwork}
        previewUrl={previewUrl}
        previewLoading={previewLoading}
        cleanUrl={cleanUrl}
        hovering={hovering}
        hasMultipleVersions={hasMultipleVersions}
        previewVersions={previewVersions}
        activeVersion={activeVersion}
        submitting={submitting}
        orderId={order?.id}
        onLoadPreview={onLoadPreview}
        onSetHovering={onSetHovering}
        onSelectVersion={onSelectVersion}
        onApproveSubmit={onApproveSubmit}
        onRequestManualProof={onRequestManualProof}
        onUploadArtworkClick={onUploadArtworkClick}
        approveButtonLabel={approveButtonLabel}
      />

      {previewError && (
        <div
          className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          data-testid="preview-sticker-error"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{previewError}</span>
        </div>
      )}
    </div>
  );
};

/**
 * iter114s.28 — Zoom + pan proof inspector. The proof image scales to
 * 2.5× on hover and the cursor-position controls the transform-origin
 * so customers can drag a hand cursor across the image to inspect any
 * part of the cutline at high resolution. The clean-sticker overlay
 * fades in alongside the zoomed proof so customers also see how the
 * final cut sticker looks at the same focal point.
 *
 * Why a sub-component: hooks (`useState`) can't live inside the parent
 * `PreviewContent` early-returns. Keeping it isolated also lets us
 * memoize the heavy mouse-move handler in one place.
 */
const ZoomableProof = ({ previewUrl, cleanUrl, hovering, onSetHovering }) => {
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    // Clamp so the very edges still zoom toward the corner cleanly.
    setZoomPos({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    });
  };

  // Only transition the transform itself (scale in/out feels smooth);
  // transform-origin is instant so the pan tracks the cursor 1:1
  // without rubber-banding behind it.
  const baseImgStyle = {
    transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
    transform: hovering ? 'scale(2.5)' : 'scale(1)',
    transition: 'transform 180ms ease-out',
  };

  const cursorClass = hovering ? 'cursor-grabbing' : 'cursor-grab';

  return (
    <div
      className={`relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 ${cursorClass}`}
      onMouseEnter={() => onSetHovering(true)}
      onMouseLeave={() => onSetHovering(false)}
      onMouseMove={handleMouseMove}
      data-testid="preview-hover-area"
    >
      <img
        src={previewUrl}
        alt="Sticker proof preview"
        style={baseImgStyle}
        className="w-full h-auto"
        data-testid="preview-sticker-image"
        draggable={false}
      />
      {cleanUrl && (
        <img
          src={cleanUrl}
          alt="Clean sticker shot"
          style={{ ...baseImgStyle, transition: `${baseImgStyle.transition}, opacity 200ms` }}
          className={`absolute inset-0 w-full h-full object-contain bg-white pointer-events-none ${
            hovering ? 'opacity-100' : 'opacity-0'
          }`}
          data-testid="preview-clean-sticker-image"
          draggable={false}
        />
      )}
      {!hovering && (
        <div
          className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded pointer-events-none"
          data-testid="preview-zoom-hint"
        >
          Hover to zoom · move to pan the dieline
        </div>
      )}
    </div>
  );
};

/**
 * Preview body — flattened state-machine that replaces the prior 4-level
 * nested ternary. Early returns make each branch readable in isolation.
 */
const PreviewContent = ({
  hasArtwork, previewUrl, previewLoading, cleanUrl, hovering,
  hasMultipleVersions, previewVersions, activeVersion, submitting, orderId,
  onLoadPreview, onSetHovering, onSelectVersion, onApproveSubmit,
  onRequestManualProof,
  onUploadArtworkClick,
  approveButtonLabel,
}) => {
  if (!hasArtwork) {
    return (
      <div
        className="flex flex-col gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm"
        data-testid="preview-no-artwork"
      >
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-1">No artwork attached yet.</p>
            <p className="text-amber-800">
              Add your design so we can render a proof preview and print your exact artwork. Already emailed it? Skip the upload — your account manager will attach it.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pl-7">
          <Button
            onClick={onUploadArtworkClick}
            data-testid="review-upload-artwork-cta"
            className="bg-[#4A7C59] hover:bg-[#3D6A4A] text-white px-4 py-2 text-sm"
          >
            <Eye className="w-4 h-4 mr-2" />
            Upload Artwork
          </Button>
          <Button
            variant="outline"
            onClick={onApproveSubmit}
            disabled={submitting}
            data-testid="review-submit-without-artwork-btn"
            className="border-amber-300 text-amber-900 hover:bg-amber-100 px-4 py-2 text-sm"
          >
            Submit without artwork →
          </Button>
        </div>
      </div>
    );
  }

  if (!previewUrl && !previewLoading) {
    return (
      <Button
        onClick={onLoadPreview}
        data-testid="preview-sticker-btn"
        className="w-full bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] text-white py-3 text-base flex items-center justify-center gap-2"
      >
        <Eye className="w-5 h-5" />
        Generate Sticker Preview
      </Button>
    );
  }

  if (previewLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12"
        data-testid="preview-sticker-loading"
      >
        <div className="w-12 h-12 border-4 border-[#4A7C59] border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm text-gray-600">
          Generating your proof… this takes a few seconds for custom die-lines.
        </p>
      </div>
    );
  }

  if (!previewUrl) return null;

  return (
    <div className="space-y-3">
      <ZoomableProof
        previewUrl={previewUrl}
        cleanUrl={cleanUrl}
        hovering={hovering}
        onSetHovering={onSetHovering}
      />

      {/* Version history carousel — lets customer toggle between proofs */}
      {hasMultipleVersions && (
        <div
          className="border border-gray-200 rounded-lg p-3 bg-gray-50"
          data-testid="preview-version-carousel"
        >
          <div className="flex items-center gap-2 mb-2">
            <History className="w-4 h-4 text-[#4A7C59]" />
            <p className="text-xs font-semibold text-gray-700">
              Proof history ({previewVersions.length} versions)
            </p>
            <p className="text-xs text-gray-500 ml-auto">
              Click a version to compare
            </p>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {previewVersions.map((v, idx) => {
              const isActive = idx === activeVersion;
              return (
                <button
                  key={v.createdAt}
                  type="button"
                  onClick={() => onSelectVersion?.(idx)}
                  data-testid={`preview-version-pill-${idx}`}
                  className={`shrink-0 flex flex-col items-center gap-1 px-2 py-1.5 rounded-md border-2 transition-all ${
                    isActive
                      ? 'border-[#4A7C59] bg-white shadow-sm'
                      : 'border-transparent bg-white/80 hover:border-[#6FBF73]/60'
                  }`}
                >
                  <img
                    src={v.previewUrl}
                    alt={v.label}
                    className="w-16 h-20 object-contain rounded"
                    loading="lazy"
                  />
                  <span className={`text-xs font-medium ${isActive ? 'text-[#2F5D3A]' : 'text-gray-600'}`}>
                    {v.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* One-click approve: commits the order AND marks proof customer-approved.
          Label adapts to billing-mode + payment status so the customer
          always sees the exact next step on the button itself:
            - pay_now + unpaid     → "Pay $X.XX & Submit Order"
            - pay_now + authorized → "Approve & Capture $X.XX"
            - invoice              → "Submit on Net-30 Invoice"
            - default              → "Approve Proof & Submit Order"
          iter114s.40 — Operator asked to use the lighter EG3 brand
          green (#6FBF73) here AND on the Manual-PDF-Proof button so
          both terminal CTAs read as one cohesive group. The previous
          amber outline on the manual-proof button is retired in
          favour of a matching solid fill at slightly reduced visual
          weight.
      */}
      <Button
        onClick={onApproveSubmit}
        disabled={submitting}
        data-testid="approve-and-submit-btn"
        className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-4 text-base flex items-center justify-center gap-2 shadow-md"
      >
        <CheckCircle2 className="w-5 h-5" />
        {approveButtonLabel}
      </Button>
      <p className="text-xs text-gray-500 text-center -mt-1">
        Approving here skips the separate request step and flags your order as
        customer-approved.
      </p>

      {/* iter114s.28 — Manual-proof escape hatch. Customer doesn't trust
          the system-generated dieline → they can submit the order with
          a flag asking fulfilment to send a hand-prepared PDF proof
          before the print run starts. Sits BELOW the green approve
          button so the choice is explicit, not buried.
          iter114s.40 — Restyled to match the Submit-on-Net-30 button
          (lighter EG3 green solid fill) per operator request. */}
      {onRequestManualProof && (
        <>
          <Button
            onClick={onRequestManualProof}
            disabled={submitting}
            data-testid="request-manual-proof-btn"
            className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-4 text-base flex items-center justify-center gap-2 shadow-md"
          >
            <FileEdit className="w-5 h-5" />
            Request Manual PDF Proof Instead
          </Button>
          <p className="text-xs text-gray-500 text-center -mt-1 leading-relaxed px-2">
            Reject the system-generated dieline and ask for a manual PDF proof
            instead. Move forward with the initial order submission, then pause
            for revised proof approval prior to completing the full order.
          </p>
        </>
      )}

      {/* iter114s.40 — "Regenerate Preview" button removed. Operator
          asked to scrub it from the proof-approval CTA group; the
          underlying onLoadPreview handler is still imported by the
          parent in case a future "Refresh" affordance gets re-added
          elsewhere on the page. */}
    </div>
  );
};
